import { Steps } from "antd";
import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import ReactQuill from "react-quill-new";
import { MdDeleteOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import GoogleMapInput from "../../components/GoogleMapInput";
import Input from "../../components/input";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import {
  fetchBanksDropdown,
  fetchPartsUnitsDropdown,
  fetchTransporterCitiesDropdown,
  fetchUsersDropdown,
  fetchCostCenterDropdown,
} from "../../store/Slices/dropdownSlice";
import { createCustomer } from "../../store/Slices/CustomerSlice";
import "./Customer.css";
import { formats, modules } from "../../utils/helper";
import { errorNotify, successNotify } from "../../utils/toast";
import { apiService } from "../../services/apiService";
import { buildPhoneNumberSchema } from "./shared/phoneValidation";
import {
  detectPakistanProvince as detectProvince,
  normalizeProvinceValue,
} from "../../utils/provinceOptions";
import {
  cityOptions,
  statusOptions,
  industryTypeOptions,
  designationOptions,
  billingFrequencyOptions,
  paymentMethodOptions,
  paymentTermOptions,
  productTypeOptions,
  insuranceCoverTypeOptions,
  insuranceStatusOptions,
  locationStatusOptions,
  resolveWarehouseStatus,
  fallbackBankOptions,
  stepItems,
} from "./customerEnums";

const toText = (value) => String(value ?? "").trim();
const resolveCoordinateText = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const defaultNotes = "";

let tempIdCounter = 1;
const createTempId = () => Date.now() + tempIdCounter++;

const createSecondary = () => ({
  id: createTempId(),
  name: "",
  contact: "",
  designation: "",
  email: "",
});

const createWarehouse = () => ({
  id: createTempId(),
  warehouseName: "",
  city: "",
  province: "",
  contact: "",
  contactPersonName: "",
  contactPersonNumber: "",
  warehouseAddress: "",
  warehouseLatitude: "",
  warehouseLongitude: "",
  typesOfGoods: [],
  status: "",
});

const createProduct = () => ({
  id: createTempId(),
  name: "",
  unit: "",
  productType: "",
  productValue: "",
  insuranceCoverType: "",
  insuranceStatus: "",
  insuranceCoverValue: "",
});

const minimumWarehouseCount = 2;
const minimumProductCount = 1;
const fallbackCustomerIdPrefix = "TPC-";

const ensureMinimumWarehouses = (warehouses = []) => {
  const nextWarehouses = Array.isArray(warehouses) ? warehouses : [];
  if (nextWarehouses.length >= minimumWarehouseCount) return nextWarehouses;

  return [
    ...nextWarehouses,
    ...Array.from(
      { length: minimumWarehouseCount - nextWarehouses.length },
      () => createWarehouse()
    ),
  ];
};

const ensureMinimumProducts = (products = []) => {
  const nextProducts = Array.isArray(products) ? products : [];
  if (nextProducts.length >= minimumProductCount) return nextProducts;

  return [
    ...nextProducts,
    ...Array.from(
      { length: minimumProductCount - nextProducts.length },
      () => createProduct()
    ),
  ];
};

const defaultValues = () => ({
  customerId: "",
  customerName: "",
  contact: "",
  email: "",
  headquarter: "",
  address: "",
  addressLatitude: "",
  addressLongitude: "",
  ntn_number: "",
  sntn_number: "",
  industryType: "",
  typesOfGoods: [],
  status: "",
  costCenterId: "",
  primaryContact: { name: "", contact: "", designation: "", email: "" },
  secondaryContacts: [],
  noWarehouses: false,
  warehouses: ensureMinimumWarehouses(),
  products: ensureMinimumProducts(),
  attachments: [],
  payment: {
    billingFrequency: "",
    paymentMethod: "",
    paymentTerm: "",
    bank: "",
    accountTitle: "",
    iban: "",
  },
  notes: defaultNotes,
});

const clone = (value) => JSON.parse(JSON.stringify(value));
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) => String(value || "").trim().toLowerCase();
const unique = (value) => [...new Set(safeArray(value).filter(Boolean))];
const cleanText = (value) => String(value || "").trim();
const firstFilled = (...values) => values.map(cleanText).find(Boolean) || "";
const toSentenceCase = (value) => {
  const text = cleanText(value).replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};
const stripHtml = (value) =>
  cleanText(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
  );

const toOptionalNumber = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "";
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : normalizedValue;
};

const touhcedFromErrors = (errors) => {
  if (Array.isArray(errors)) return errors.map((item) => touchedFromErrors(item));
  if (errors && typeof errors === "object") {
    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = touchedFromErrors(errors[key]);
      return acc;
    }, {});
  }
  return true;
};
const touchedFromErrors = touhcedFromErrors;

const extractIdMeta = (value) => {
  const text = cleanText(value);
  if (!text) return null;

  const match = text.match(/^(.*?)(\d+)$/);
  if (!match) return null;

  const number = Number(match[2]);
  if (!Number.isFinite(number)) return null;

  return {
    prefix: match[1],
    number,
    padding: match[2].length,
  };
};

const generateNextCustomerId = (rows = []) => {
  let highestCustomerId = null;
  let highestRecordId = 0;

  safeArray(rows).forEach((item) => {
    const customerIdMeta = extractIdMeta(item?.customer_id ?? item?.customerId);
    if (
      customerIdMeta &&
      (!highestCustomerId || customerIdMeta.number > highestCustomerId.number)
    ) {
      highestCustomerId = customerIdMeta;
    }

    const recordId = Number(item?.id ?? item?.recordId);
    if (Number.isFinite(recordId) && recordId > highestRecordId) {
      highestRecordId = recordId;
    }
  });

  if (highestCustomerId) {
    return `${highestCustomerId.prefix}${String(
      highestCustomerId.number + 1
    ).padStart(highestCustomerId.padding, "0")}`;
  }

  return `${fallbackCustomerIdPrefix}${String(
    Math.max(highestRecordId + 1, 1)
  ).padStart(4, "0")}`;
};

const resolveOptionText = (options, selectedValue) => {
  const selected = cleanText(selectedValue);
  if (!selected) return "";

  const matched = safeArray(options).find(
    (option) =>
      normalize(option?.value) === normalize(selected) ||
      normalize(option?.label) === normalize(selected)
  );

  return cleanText(matched?.label ?? selectedValue);
};

const toCreateCustomerPayload = (values, unitOptions = []) => {
  const warehouses = safeArray(values.warehouses);
  const products = safeArray(values.products);

  const pocDetails = [
    {
      name: cleanText(values.primaryContact?.name),
      email: cleanText(values.primaryContact?.email),
      contact: cleanText(values.primaryContact?.contact),
      designation: cleanText(values.primaryContact?.designation),
      is_primary: true,
    },
    ...safeArray(values.secondaryContacts).map((item) => ({
      name: cleanText(item?.name),
      email: cleanText(item?.email),
      contact: cleanText(item?.contact),
      designation: cleanText(item?.designation),
      is_primary: false,
    })),
  ].filter((item) => item.name || item.email || item.contact || item.designation);

  return {
    name: cleanText(values.customerName),
    contact: cleanText(values.contact),
    email: cleanText(values.email),
    status: cleanText(values.status).toLowerCase(),
    industry_type: cleanText(values.industryType),
    note: stripHtml(values.notes),
    poc_details: pocDetails,
    ntn_number: cleanText(values.ntn_number),
    sntn_number: cleanText(values.sntn_number),
    address: cleanText(values.address),
    cost_center_id: values.costCenterId ? Number(values.costCenterId) : null,
    has_warehouses: warehouses.length > 0,
    warehouses: warehouses.map((item) => ({
      warehouse_name: cleanText(item?.warehouseName),
      city: toSentenceCase(item?.city),
      contact: cleanText(item?.contact),
      contact_person_name: cleanText(item?.contactPersonName),
      contact_person_number: cleanText(item?.contactPersonNumber),
      address: cleanText(item?.warehouseAddress),
      lat: cleanText(item?.warehouseLatitude),
      lng: cleanText(item?.warehouseLongitude),
      province: normalizeProvinceValue(item?.province),
      status: cleanText(item?.status || "active").toLowerCase(),
    })),
    products: products.map((item) => ({
      name: cleanText(item?.name),
      unit: resolveOptionText(unitOptions, item?.unit),
      product_type: cleanText(item?.productType),
      product_value: toOptionalNumber(item?.productValue),
      insurance_cover_type: cleanText(item?.insuranceCoverType),
      insurance_status: cleanText(item?.insuranceStatus),
      insurance_cover_value: toOptionalNumber(item?.insuranceCoverValue),
    })),
    files: unique(
      safeArray(values.attachments)
        .map((file) => {
          if (typeof file === "string") return cleanText(file);
          if (!file || typeof file !== "object") return "";
          return cleanText(
            file?.file_path ||
            file?.fileName ||
            file?.file_name ||
            file?.path ||
            file?.url ||
            file?.file ||
            file?.image ||
            file?.originalName ||
            ""
          );
        })
        .filter(Boolean)
    ),
    payment_method: cleanText(values.payment?.paymentMethod),
    payment_terms: cleanText(values.payment?.paymentTerm),
    billing_frequency: cleanText(values.payment?.billingFrequency),
    bank_name: cleanText(values.payment?.bank),
    account_title: cleanText(values.payment?.accountTitle),
    iban_number: cleanText(values.payment?.iban),
  };
};

const resolveAddressCoordinates = async (address) => {
  const normalizedAddress = cleanText(address);
  if (!normalizedAddress) return { lat: "", lng: "" };

  try {
    const results = await geocodeByAddress(normalizedAddress);
    const coordinates = await getLatLng(results[0]);

    return {
      lat: cleanText(coordinates?.lat),
      lng: cleanText(coordinates?.lng),
    };
  } catch (error) {
    return { lat: "", lng: "" };
  }
};

const withResolvedWarehouseCoordinates = async (values = {}) => {
  const warehouses = await Promise.all(
    safeArray(values.warehouses).map(async (warehouse) => {
      const existingLatitude = cleanText(warehouse?.warehouseLatitude);
      const existingLongitude = cleanText(warehouse?.warehouseLongitude);

      if (existingLatitude && existingLongitude) {
        return warehouse;
      }

      const coordinates = await resolveAddressCoordinates(warehouse?.warehouseAddress);

      return {
        ...warehouse,
        warehouseLatitude: existingLatitude || coordinates.lat,
        warehouseLongitude: existingLongitude || coordinates.lng,
      };
    })
  );

  return {
    ...values,
    warehouses,
  };
};

const stepSchemas = [
  Yup.object().shape({
    customerName: Yup.string().trim().required("Customer Name is required"),
    contact: buildPhoneNumberSchema("Contact is required"),
    email: Yup.string()
      .trim()
      .required("Email Address is required")
      .email("Invalid email address"),
    industryType: Yup.string().required("Industry Type is required"),
    status: Yup.string().required("Status is required"),
    ntn_number: Yup.string()
      .trim()
      .required("NTN Number is required")
      .matches(/^\d{7}-?\d{1}$/, "NTN must be in format 1234567-8 or 12345678"),
    sntn_number: Yup.string()
      .trim()
      .required("STRN Number is required")
      .matches(/^(\d{2}-\d{2}-\d{4}-\d{3}-\d{2}|\d{13})$/, "STRN must be in format 12-34-5678-901-23 or 1234567891011"),
    address: Yup.string().trim().required("Billing Address is required"),
    costCenterId: Yup.string().required("Cost Center is required"),
  }),
  Yup.object().shape({
    primaryContact: Yup.object().shape({
      name: Yup.string().trim().required("Primary contact name is required"),
      contact: buildPhoneNumberSchema("Primary contact number is required"),
      designation: Yup.string().required("Primary designation is required"),
      email: Yup.string()
        .trim()
        .required("Primary email is required")
        .email("Invalid email address"),
    }),
    secondaryContacts: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().required("Secondary contact name is required"),
        contact: buildPhoneNumberSchema("Secondary contact number is required"),
        designation: Yup.string().required("Secondary designation is required"),
        email: Yup.string()
          .trim()
          .nullable()
          .transform((v, o) => (String(o || "").trim() ? v : null))
          .email("Invalid email address"),
      })
    ),
  }),
  Yup.object().shape({
    warehouses: Yup.array()
      .of(
        Yup.object().shape({
          warehouseName: Yup.string()
            .trim()
            .required("Location is required"),
          city: Yup.string().required("City is required"),
          province: Yup.string().nullable().notRequired(),
          contact: buildPhoneNumberSchema("Contact is required"),
          contactPersonName: Yup.string()
            .trim()
            .required("Contact Person Name is required"),
          contactPersonNumber: buildPhoneNumberSchema(
            "Contact Person Number is required"
          ),
          warehouseAddress: Yup.string()
            .trim()
            .required("Address is required"),
          status: Yup.string().required("Status is required"),
        })
      )
      .min(
        minimumWarehouseCount,
        `At least ${minimumWarehouseCount} locations are required`
      ),
  }),
  Yup.object().shape({
    products: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().trim().required("Product Name is required"),
          unit: Yup.mixed().test(
            "unit-required",
            "Unit is required",
            (value) => String(value ?? "").trim() !== ""
          ),
          productType: Yup.string().trim().required("Product Type is required"),
          productValue: Yup.number()
            .transform((value, originalValue) =>
              String(originalValue ?? "").trim() === "" ? undefined : value
            )
            .typeError("Product Value is required")
            .required("Product Value is required")
            .min(0, "Product Value cannot be negative"),
          insuranceCoverType: Yup.string()
            .trim()
            .required("Insurance Cover Type is required"),
          insuranceStatus: Yup.string()
            .trim()
            .required("Insurance Status is required"),
          insuranceCoverValue: Yup.number()
            .transform((value, originalValue) =>
              String(originalValue ?? "").trim() === "" ? undefined : value
            )
            .typeError("Insurance Cover Value is required")
            .required("Insurance Cover Value is required")
            .min(0, "Insurance Cover Value cannot be negative")
            .max(100, "Insurance Cover Value cannot exceed 100%"),
        })
      )
      .min(minimumProductCount, "At least one product is required"),
  }),
  Yup.object().shape({
    attachments: Yup.array(),
  }),
  Yup.object().shape({
    payment: Yup.object().shape({
      billingFrequency: Yup.string().required("Billing Frequency is required"),
      paymentMethod: Yup.string()
        .oneOf(["bank_transfer", "cash"], "Payment Method is required")
        .required("Payment Method is required"),
      paymentTerm: Yup.string().when("paymentMethod", {
        is: (method) => ["bank_transfer", "cash"].includes(method),
        then: (schema) => schema.required("Payment Term is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      bank: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      accountTitle: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Account Title is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      iban: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("IBAN / Account Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
];

const AddCustomer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    banksDropdownData = [],
    partsUnitsDropdownData = [],
    transporterCitiesDropdownData = [],
    costCenterDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns);
  const { createCustomerLoading } = useSelector((state) => state.customer);

  const [currentStep, setCurrentStep] = useState(0);
  const [initialValues, setInitialValues] = useState(defaultValues());

  useEffect(() => {
    dispatch(fetchBanksDropdown());
    dispatch(fetchPartsUnitsDropdown());
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchUsersDropdown());
    dispatch(fetchCostCenterDropdown());
  }, [dispatch]);

  useEffect(() => {
    let ignore = false;

    const loadInitialValues = async () => {
      const nextValues = defaultValues();
      try {
        const initialListResponse = await apiService.getCustomersPaginated({
          page: 1,
          limit: 1000,
        });

        let customerRows = safeArray(
          initialListResponse?.data?.results || initialListResponse?.results
        );
        const totalItems = Number(
          initialListResponse?.data?.meta?.totalItems ||
          initialListResponse?.meta?.totalItems
        );

        if (Number.isFinite(totalItems) && totalItems > customerRows.length) {
          const fullListResponse = await apiService.getCustomersPaginated({
            page: 1,
            limit: totalItems,
          });
          customerRows = safeArray(
            fullListResponse?.data?.results || fullListResponse?.results
          );
        }

        nextValues.customerId = generateNextCustomerId(customerRows);
      } catch (error) {
        nextValues.customerId = generateNextCustomerId();
      }

      if (!ignore) {
        setInitialValues(nextValues);
        setCurrentStep(0);
      }
    };

    loadInitialValues();

    return () => {
      ignore = true;
    };
  }, [dispatch]);

  const bankOptions = useMemo(
    () => (banksDropdownData?.length ? banksDropdownData : fallbackBankOptions),
    [banksDropdownData]
  );

  const getOptionLabel = (item) =>
    String(
      item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value ?? ""
    ).trim();

  const warehouseCityOptions = useMemo(() => {
    const source = transporterCitiesDropdownData?.length
      ? transporterCitiesDropdownData
      : cityOptions;
    return source
      .map((item) => {
        const label = getOptionLabel(item);
        if (!label) return null;
        return { label, value: normalize(label) };
      })
      .filter(Boolean);
  }, [transporterCitiesDropdownData]);

  const partUnitOptions = useMemo(
    () => safeArray(partsUnitsDropdownData),
    [partsUnitsDropdownData]
  );

  const pickOption = (options, selectedValue) => {
    const selected = String(selectedValue || "");
    return (
      options.find(
        (option) =>
          normalize(option?.value) === normalize(selected) ||
          normalize(option?.label) === normalize(selected)
      ) || null
    );
  };

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const valuesWithCoordinates = await withResolvedWarehouseCoordinates(values);
      const payload = toCreateCustomerPayload(valuesWithCoordinates, partUnitOptions);
      await dispatch(createCustomer(payload)).unwrap();
      successNotify("Customer created successfully");
      navigate("/customers");
    } catch (error) {
      errorNotify(error || "Failed to save customer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={stepSchemas[currentStep]}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        validateForm,
        setTouched,
        isSubmitting,
      }) => {
        const isLastStep = currentStep === stepItems.length - 1;
        const hasSecondaryContacts = safeArray(values.secondaryContacts).length > 0;
        const deleteActionStyle = {
          border: "none",
          background: "transparent",
          color: "#EF0000",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          fontSize: 15,
          fontWeight: 400,
          marginTop: 2,
        };
        const addActionBaseStyle = {
          width: "100%",
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          fontSize: 13,
          fontWeight: 400,
        };
        const getAddActionStyle = (disabled = false, extraStyles = {}) => ({
          ...addActionBaseStyle,
          border: disabled ? "1px solid #BDBDBD" : "1px solid #D3DEEE",
          background: disabled ? "#BDBDBD" : "#F4F7FD",
          color: disabled ? "#F2F2F2" : "#1447B1",
          cursor: disabled ? "not-allowed" : "pointer",
          ...extraStyles,
        });

        const requiredStar = (
          <span style={{ color: "red", fontSize: 13 }}>*</span>
        );
        const requiredLabel = (text, isRequired = true) =>
          isRequired ? (
            <>
              {text} {requiredStar}
            </>
          ) : (
            text
          );
        const selectMenuPortalTarget =
          typeof document !== "undefined" ? document.body : null;

        const nextStep = async () => {
          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep((prev) => Math.min(prev + 1, stepItems.length - 1));
        };

        const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

        const gotoStep = async (target) => {
          if (target <= currentStep) {
            setCurrentStep(target);
            return;
          }

          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep(target);
        };

        const addSecondary = () => {
          setFieldValue("secondaryContacts", [
            ...safeArray(values.secondaryContacts),
            createSecondary(),
          ]);
        };

        const removeSecondary = (id) => {
          const nextItems = safeArray(values.secondaryContacts).filter(
            (item) => item.id !== id
          );
          setFieldValue("secondaryContacts", nextItems);
        };

        const addWarehouseRow = () => {
          setFieldValue("warehouses", [...safeArray(values.warehouses), createWarehouse()]);
        };

        const removeWarehouseRow = (id) => {
          const nextItems = safeArray(values.warehouses).filter(
            (item) => item.id !== id
          );
          if (nextItems.length < minimumWarehouseCount) return;
          setFieldValue("warehouses", nextItems);
        };

        const addProductRow = () => {
          setFieldValue("products", [...safeArray(values.products), createProduct()]);
        };

        const removeProductRow = (id) => {
          const nextItems = safeArray(values.products).filter(
            (item) => item.id !== id
          );
          if (nextItems.length < minimumProductCount) return;
          setFieldValue("products", nextItems);
        };

        return (
          <>
            <Breadcrumbs
              values={[
                "Customers",
                "Add Customer",
              ]}
            />

            <div className="page_header access_view">
              <h2>Add Customer</h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/customers")}
                  shouldRow={false}
                  isShowSubmitBtn={false}
                  cancelBtnText="Cancel"
                />
                <Button
                  submitText="Save"
                  submitBtn={handleSubmit}
                  loading={isSubmitting || createCustomerLoading}
                  disabled={
                    !isLastStep ||
                    isSubmitting ||
                    createCustomerLoading
                  }
                  shouldRow={false}
                  isShowCancel={false}
                  isShowSubmitBtn={true}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Customer Details</h5>
                    <div>
                      <h6>
                        Customer ID: <span>{values.customerId || "--"}</span>
                      </h6>
                      <div className="d-flex">
                        <div style={{ minWidth: "175px" }}>
                          <Steps
                            className="transporter_steps form_steps_vertical"
                            current={currentStep}
                            direction="vertical"
                            items={stepItems}
                            onChange={gotoStep}
                          />
                        </div>
                        <div className="w-100 mb-3">
                          {currentStep === 0 && (
                            <div>
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Customer Name"
                                    placeholder="Enter Name"
                                    name="customerName"
                                    value={values.customerName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.customerName}
                                    touched={touched.customerName}
                                  />
                                </Col>
                                <Col md={6}>
                                  <PhoneNumberInput
                                    label="Contact"
                                    name="contact"
                                    value={values.contact}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    error={errors.contact}
                                    touched={touched.contact}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Email Address"
                                    placeholder="Enter Email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.email}
                                    touched={touched.email}
                                    required
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Cost Center")}
                                    placeholder="Select Cost Center"
                                    options={costCenterDropdownData}
                                    value={costCenterDropdownData?.find((o) => String(o.value) === String(values.costCenterId)) || null}
                                    name="costCenterId"
                                    onChange={(opt) =>
                                      setFieldValue("costCenterId", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("costCenterId", true)}
                                    error={errors.costCenterId}
                                    touched={touched.costCenterId}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Industry Type")}
                                    placeholder="Select Industry"
                                    options={industryTypeOptions}
                                    value={pickOption(industryTypeOptions, values.industryType)}
                                    name="industryType"
                                    onChange={(opt) =>
                                      setFieldValue("industryType", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("industryType", true)}
                                    error={errors.industryType}
                                    touched={touched.industryType}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Status")}
                                    placeholder="Select Status"
                                    options={statusOptions}
                                    value={pickOption(statusOptions, values.status)}
                                    name="status"
                                    onChange={(opt) =>
                                      setFieldValue("status", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("status", true)}
                                    error={errors.status}
                                    touched={touched.status}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label={requiredLabel("NTN Number")}
                                    placeholder="1234567-8"
                                    name="ntn_number"
                                    value={values.ntn_number}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9-]/g, "");
                                      setFieldValue("ntn_number", val);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.ntn_number}
                                    touched={touched.ntn_number}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label={requiredLabel("STRN Number")}
                                    placeholder="12-34-5678-901-23"
                                    name="sntn_number"
                                    value={values.sntn_number}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9-]/g, "");
                                      setFieldValue("sntn_number", val);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.sntn_number}
                                    touched={touched.sntn_number}
                                  />
                                </Col>
                                <Col md={12}>
                                  <Input
                                    label={requiredLabel("Billing Address")}
                                    placeholder="Enter Registered / Billing Address"
                                    name="address"
                                    value={values.address}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.address}
                                    touched={touched.address}
                                  />
                                </Col>
                                <Col md={12}>
                                  <div className="d-flex align-items-center gap-2">
                                    <button type="button" className="cancel_btn" disabled>
                                      Previous
                                    </button>
                                    <button
                                      type="button"
                                      className="main_btn"
                                      onClick={nextStep}
                                    >
                                      Next
                                    </button>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}

                          {currentStep === 1 && (
                            <div>
                              <h6
                                style={{
                                  background: "transparent",
                                  padding: 0,
                                  marginBottom: 10,
                                  color: "#3D3F5C",
                                  fontSize: 24,
                                  fontWeight: 600,
                                }}
                              >
                                Primary Point of Contact
                              </h6>
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Name"
                                    placeholder="Enter Name"
                                    name="primaryContact.name"
                                    value={values.primaryContact.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.name}
                                    touched={touched.primaryContact?.name}
                                  />
                                </Col>
                                <Col md={6}>
                                  <PhoneNumberInput
                                    label="Contact"
                                    name="primaryContact.contact"
                                    value={values.primaryContact.contact}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.contact}
                                    touched={touched.primaryContact?.contact}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Designation")}
                                    placeholder="Select Designation"
                                    options={designationOptions}
                                    value={
                                      designationOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          values.primaryContact.designation
                                      ) || null
                                    }
                                    name="primaryContact.designation"
                                    onChange={(opt) =>
                                      setFieldValue(
                                        "primaryContact.designation",
                                        opt ? opt.value : ""
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched(
                                        "primaryContact.designation",
                                        true
                                      )
                                    }
                                    error={errors.primaryContact?.designation}
                                    touched={touched.primaryContact?.designation}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Email Address"
                                    placeholder="Enter Email"
                                    name="primaryContact.email"
                                    value={values.primaryContact.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.primaryContact?.email}
                                    touched={touched.primaryContact?.email}
                                  />
                                </Col>
                              </Row>

                              {hasSecondaryContacts && (
                                <div
                                  style={{
                                    borderTop: "1px solid #DDE3F0",
                                    marginTop: 16,
                                    marginBottom: 14,
                                  }}
                                />
                              )}

                              {safeArray(values.secondaryContacts).map((item, index) => (
                                <div key={item.id} style={{ marginBottom: 12 }}>
                                  <h6
                                    style={{
                                      background: "transparent",
                                      padding: 0,
                                      marginBottom: 10,
                                      color: "#3D3F5C",
                                      fontSize: 20,
                                      fontWeight: 500,
                                    }}
                                  >
                                    Secondary Point of Contact
                                  </h6>
                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Name"
                                        placeholder="Enter Name"
                                        name={`secondaryContacts[${index}].name`}
                                        value={item.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.name}
                                        touched={touched.secondaryContacts?.[index]?.name}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        label="Contact"
                                        name={`secondaryContacts[${index}].contact`}
                                        value={item.contact}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.contact}
                                        touched={touched.secondaryContacts?.[index]?.contact}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Designation")}
                                        placeholder="Select Designation"
                                        options={designationOptions}
                                        value={
                                          designationOptions.find(
                                            (opt) => opt.value === item.designation
                                          ) || null
                                        }
                                        name={`secondaryContacts[${index}].designation`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `secondaryContacts[${index}].designation`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `secondaryContacts[${index}].designation`,
                                            true
                                          )
                                        }
                                        error={errors.secondaryContacts?.[index]?.designation}
                                        touched={
                                          touched.secondaryContacts?.[index]?.designation
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Email Address"
                                        placeholder="Enter Email"
                                        name={`secondaryContacts[${index}].email`}
                                        value={item.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.secondaryContacts?.[index]?.email}
                                        touched={touched.secondaryContacts?.[index]?.email}
                                        required={false}
                                      />
                                    </Col>
                                  </Row>

                                  <button
                                    type="button"
                                    onClick={() => removeSecondary(item.id)}
                                    style={deleteActionStyle}
                                  >
                                    <MdDeleteOutline style={{ fontSize: 18 }} />
                                    Delete Contact Person
                                  </button>

                                  {index !== safeArray(values.secondaryContacts).length - 1 && (
                                    <div
                                      style={{
                                        borderTop: "1px solid #DDE3F0",
                                        marginTop: 14,
                                        marginBottom: 14,
                                      }}
                                    />
                                  )}
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={addSecondary}
                                style={getAddActionStyle(false, { marginTop: 14 })}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Contact Person
                              </button>

                              <div className="d-flex align-items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={prevStep}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  className="main_btn"
                                  onClick={nextStep}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}

                          {currentStep === 2 && (
                            <div>
                              <p
                                style={{
                                  color: "#3D3F5C",
                                  fontSize: 15,
                                  marginBottom: 10,
                                }}
                              >
                                Add at least {minimumWarehouseCount} customer locations
                                by city/address. You can add more if needed.
                              </p>

                              <div
                                style={{
                                  borderTop: "1px solid #DDE3F0",
                                  marginTop: 8,
                                  marginBottom: 18,
                                }}
                              />

                              {safeArray(values.warehouses).map((item, index) => (
                                <div key={item.id} style={{ marginBottom: 12 }}>
                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Location Name"
                                        placeholder="Enter Location"
                                        name={`warehouses[${index}].warehouseName`}
                                        value={item.warehouseName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          errors.warehouses?.[index]?.warehouseName
                                        }
                                        touched={
                                          touched.warehouses?.[index]?.warehouseName
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("City")}
                                        placeholder="Select City"
                                        options={warehouseCityOptions}
                                        loading={dropdownLoading}
                                        value={pickOption(
                                          warehouseCityOptions,
                                          item.city
                                        )}
                                        name={`warehouses[${index}].city`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `warehouses[${index}].city`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `warehouses[${index}].city`,
                                            true
                                          )
                                        }
                                        error={errors.warehouses?.[index]?.city}
                                        touched={
                                          touched.warehouses?.[index]?.city
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        label="Contact"
                                        name={`warehouses[${index}].contact`}
                                        value={item.contact}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={
                                          errors.warehouses?.[index]?.contact
                                        }
                                        touched={
                                          touched.warehouses?.[index]?.contact
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Contact Person Name"
                                        placeholder="Enter Contact Person Name"
                                        name={`warehouses[${index}].contactPersonName`}
                                        value={item.contactPersonName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          errors.warehouses?.[index]
                                            ?.contactPersonName
                                        }
                                        touched={
                                          touched.warehouses?.[index]
                                            ?.contactPersonName
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        label="Contact Person Number"
                                        name={`warehouses[${index}].contactPersonNumber`}
                                        value={item.contactPersonNumber}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={
                                          errors.warehouses?.[index]
                                            ?.contactPersonNumber
                                        }
                                        touched={
                                          touched.warehouses?.[index]
                                            ?.contactPersonNumber
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Status")}
                                        placeholder="Select Status"
                                        options={locationStatusOptions}
                                        value={
                                          locationStatusOptions.find(
                                            (opt) =>
                                              opt.value ===
                                              resolveWarehouseStatus(item.status)
                                          ) || null
                                        }
                                        name={`warehouses[${index}].status`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `warehouses[${index}].status`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `warehouses[${index}].status`,
                                            true
                                          )
                                        }
                                        error={errors.warehouses?.[index]?.status}
                                        touched={touched.warehouses?.[index]?.status}
                                      />
                                    </Col>
                                    <Col md={12}>
                                      <GoogleMapInput
                                        label="Address"
                                        placeholder="Search address..."
                                        name={`warehouses[${index}].warehouseAddress`}
                                        value={item.warehouseAddress}
                                        setFieldValue={setFieldValue}
                                        onChange={(address, latitude, longitude, rawResult) => {
                                          setFieldTouched(
                                            `warehouses[${index}].warehouseAddress`,
                                            false,
                                            false
                                          );
                                          setFieldValue(
                                            `warehouses[${index}].warehouseAddress`,
                                            address || ""
                                          );
                                          setFieldValue(
                                            `warehouses[${index}].warehouseLatitude`,
                                            latitude || ""
                                          );
                                          setFieldValue(
                                            `warehouses[${index}].warehouseLongitude`,
                                            longitude || ""
                                          );
                                          const resolvedProv = detectProvince(address, rawResult);
                                          if (resolvedProv) {
                                            setFieldValue(
                                              "warehouses[" + index + "].province",
                                              resolvedProv
                                            );
                                          }
                                        }}
                                        error={
                                          errors.warehouses?.[index]
                                            ?.warehouseAddress
                                        }
                                        touched={
                                          touched.warehouses?.[index]
                                            ?.warehouseAddress
                                        }
                                        storeAs="string"
                                        latitudeFieldName={`warehouses[${index}].warehouseLatitude`}
                                        longitudeFieldName={`warehouses[${index}].warehouseLongitude`}
                                      />
                                    </Col>
                                  </Row>

                                  {safeArray(values.warehouses).length >
                                    minimumWarehouseCount && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeWarehouseRow(item.id)
                                        }
                                        style={deleteActionStyle}
                                      >
                                        <MdDeleteOutline
                                          style={{ fontSize: 18 }}
                                        />
                                        Delete Location
                                      </button>
                                    )}

                                  <div
                                    style={{
                                      borderTop: "1px solid #DDE3F0",
                                      marginTop: 14,
                                      marginBottom: 14,
                                    }}
                                  />
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={addWarehouseRow}
                                style={getAddActionStyle(false)}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Customer Location
                              </button>

                              {touched.warehouses &&
                                typeof errors.warehouses === "string" && (
                                  <div
                                    className="error-message"
                                    style={{
                                      color: "#ff0000de",
                                      fontSize: 12,
                                      marginTop: 4,
                                    }}
                                  >
                                    {errors.warehouses}
                                  </div>
                                )}

                              <div className="d-flex align-items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={prevStep}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  className="main_btn"
                                  onClick={nextStep}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}

                          {currentStep === 3 && (
                            <div>
                              <p
                                style={{
                                  color: "#3D3F5C",
                                  fontSize: 15,
                                  marginBottom: 10,
                                }}
                              >
                                Add customer product details. You can add more products if
                                needed.
                              </p>

                              <div
                                style={{
                                  borderTop: "1px solid #DDE3F0",
                                  marginTop: 8,
                                  marginBottom: 18,
                                }}
                              />

                              {safeArray(values.products).map((item, index) => (
                                <div key={item.id} style={{ marginBottom: 12 }}>
                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Product Name"
                                        placeholder="Enter Product Name"
                                        name={`products[${index}].name`}
                                        value={item.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.products?.[index]?.name}
                                        touched={touched.products?.[index]?.name}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Unit")}
                                        placeholder="Select Unit"
                                        options={partUnitOptions}
                                        loading={dropdownLoading}
                                        value={pickOption(partUnitOptions, item.unit)}
                                        name={`products[${index}].unit`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `products[${index}].unit`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(`products[${index}].unit`, true)
                                        }
                                        error={errors.products?.[index]?.unit}
                                        touched={touched.products?.[index]?.unit}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Product Type")}
                                        placeholder="Select Product Type"
                                        options={productTypeOptions}
                                        value={pickOption(productTypeOptions, item.productType)}
                                        name={`products[${index}].productType`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `products[${index}].productType`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `products[${index}].productType`,
                                            true
                                          )
                                        }
                                        error={errors.products?.[index]?.productType}
                                        touched={touched.products?.[index]?.productType}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Product Value"
                                        type="number"
                                        placeholder="Enter Product Value"
                                        name={`products[${index}].productValue`}
                                        value={item.productValue}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.products?.[index]?.productValue}
                                        touched={touched.products?.[index]?.productValue}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Insurance Cover Type")}
                                        placeholder="Select Insurance Cover Type"
                                        options={insuranceCoverTypeOptions}
                                        value={pickOption(
                                          insuranceCoverTypeOptions,
                                          item.insuranceCoverType
                                        )}
                                        name={`products[${index}].insuranceCoverType`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `products[${index}].insuranceCoverType`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `products[${index}].insuranceCoverType`,
                                            true
                                          )
                                        }
                                        error={errors.products?.[index]?.insuranceCoverType}
                                        touched={
                                          touched.products?.[index]?.insuranceCoverType
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Insurance Status")}
                                        placeholder="Select Insurance Status"
                                        options={insuranceStatusOptions}
                                        value={pickOption(
                                          insuranceStatusOptions,
                                          item.insuranceStatus
                                        )}
                                        name={`products[${index}].insuranceStatus`}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `products[${index}].insuranceStatus`,
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            `products[${index}].insuranceStatus`,
                                            true
                                          )
                                        }
                                        error={errors.products?.[index]?.insuranceStatus}
                                        touched={touched.products?.[index]?.insuranceStatus}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Insurance Cover Value"
                                        type="text"
                                        placeholder="Enter Insurance Cover Value"
                                        name={`products[${index}].insuranceCoverValue`}
                                        value={item.insuranceCoverValue}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === "") {
                                            setFieldValue(`products[${index}].insuranceCoverValue`, "", true);
                                            return;
                                          }
                                          if (/^\d*\.?\d*$/.test(val)) {
                                            const num = parseFloat(val);
                                            if (isNaN(num) || num <= 100) {
                                              setFieldValue(`products[${index}].insuranceCoverValue`, val, true);
                                            }
                                          }
                                        }}
                                        onBlur={() =>
                                          setFieldTouched(
                                            `products[${index}].insuranceCoverValue`,
                                            true,
                                            true
                                          )
                                        }
                                        error={errors.products?.[index]?.insuranceCoverValue}
                                        touched={
                                          touched.products?.[index]?.insuranceCoverValue
                                        }
                                      />
                                    </Col>
                                  </Row>

                                  {safeArray(values.products).length > minimumProductCount && (
                                    <button
                                      type="button"
                                      onClick={() => removeProductRow(item.id)}
                                      style={deleteActionStyle}
                                    >
                                      <MdDeleteOutline style={{ fontSize: 18 }} />
                                      Delete Product
                                    </button>
                                  )}

                                  <div
                                    style={{
                                      borderTop: "1px solid #DDE3F0",
                                      marginTop: 14,
                                      marginBottom: 14,
                                    }}
                                  />
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={addProductRow}
                                style={getAddActionStyle(false)}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Customer Product
                              </button>

                              {touched.products && typeof errors.products === "string" && (
                                <div
                                  className="error-message"
                                  style={{
                                    color: "#ff0000de",
                                    fontSize: 12,
                                    marginTop: 4,
                                  }}
                                >
                                  {errors.products}
                                </div>
                              )}

                              <div className="d-flex align-items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={prevStep}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  className="main_btn"
                                  onClick={nextStep}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}

                          {currentStep === 4 && (
                            <div>
                              <p
                                style={{
                                  color: "#3D3F5C",
                                  fontSize: 15,
                                  marginBottom: 10,
                                }}
                              >
                                You can attach up to 5 files, max 1MB each.
                              </p>
                              <FileUpload
                                destination="3pl-customers"
                                files={safeArray(values.attachments)}
                                setFiles={(nextFiles) => {
                                  const resolved =
                                    typeof nextFiles === "function"
                                      ? nextFiles(safeArray(values.attachments))
                                      : nextFiles;
                                  setFieldValue("attachments", resolved);
                                }}
                                maxFiles={5}
                                maxFileSize={1}
                              />
                              {touched.attachments && errors.attachments && (
                                <div
                                  className="error-message"
                                  style={{
                                    color: "#ff0000de",
                                    fontSize: 12,
                                    marginTop: 4,
                                  }}
                                >
                                  {errors.attachments}
                                </div>
                              )}
                              <div className="d-flex align-items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={prevStep}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  className="main_btn"
                                  onClick={nextStep}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}

                          {currentStep === 5 && (
                            <div>
                              <Row>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Billing Frequency")}
                                    placeholder="Select Frequency"
                                    options={billingFrequencyOptions}
                                    value={
                                      billingFrequencyOptions.find(
                                        (opt) =>
                                          opt.value ===
                                          values.payment.billingFrequency
                                      ) || null
                                    }
                                    name="payment.billingFrequency"
                                    onChange={(opt) =>
                                      setFieldValue(
                                        "payment.billingFrequency",
                                        opt ? opt.value : ""
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched("payment.billingFrequency", true)
                                    }
                                    error={errors.payment?.billingFrequency}
                                    touched={touched.payment?.billingFrequency}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Payment Method")}
                                    placeholder="Select Method"
                                    options={paymentMethodOptions}
                                    value={
                                      paymentMethodOptions.find(
                                        (opt) =>
                                          opt.value === values.payment.paymentMethod
                                      ) || null
                                    }
                                    name="payment.paymentMethod"
                                    onChange={(opt) => {
                                      const method = opt ? opt.value : "";
                                      setFieldValue("payment.paymentMethod", method);

                                      if (!["bank_transfer", "cash"].includes(method)) {
                                        setFieldValue("payment.paymentTerm", "");
                                      }

                                      if (method !== "bank_transfer") {
                                        setFieldValue("payment.bank", "");
                                        setFieldValue("payment.accountTitle", "");
                                        setFieldValue("payment.iban", "");
                                      }
                                    }}
                                    onBlur={() =>
                                      setFieldTouched("payment.paymentMethod", true)
                                    }
                                    error={errors.payment?.paymentMethod}
                                    touched={touched.payment?.paymentMethod}
                                  />
                                </Col>

                                {["bank_transfer", "cash"].includes(
                                  values.payment.paymentMethod
                                ) && (
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Payment Term")}
                                        placeholder="Select Payment Term"
                                        options={paymentTermOptions}
                                        value={
                                          paymentTermOptions.find(
                                            (opt) =>
                                              opt.value === values.payment.paymentTerm
                                          ) || null
                                        }
                                        name="payment.paymentTerm"
                                        onChange={(opt) =>
                                          setFieldValue(
                                            "payment.paymentTerm",
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched("payment.paymentTerm", true)
                                        }
                                        menuPlacement="auto"
                                        menuPortalTarget={selectMenuPortalTarget}
                                        error={errors.payment?.paymentTerm}
                                        touched={touched.payment?.paymentTerm}
                                      />
                                    </Col>
                                  )}

                                {values.payment.paymentMethod === "bank_transfer" && (
                                  <>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Bank")}
                                        placeholder="Select Bank"
                                        options={bankOptions}
                                        loading={dropdownLoading}
                                        value={pickOption(bankOptions, values.payment.bank)}
                                        name="payment.bank"
                                        onChange={(opt) =>
                                          setFieldValue(
                                            "payment.bank",
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched("payment.bank", true)
                                        }
                                        error={errors.payment?.bank}
                                        touched={touched.payment?.bank}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Account Title"
                                        placeholder="Enter Account Title"
                                        name="payment.accountTitle"
                                        value={values.payment.accountTitle}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.payment?.accountTitle}
                                        touched={touched.payment?.accountTitle}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="IBAN / Account Number"
                                        placeholder="Enter IBAN / Account Number"
                                        name="payment.iban"
                                        value={values.payment.iban}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.payment?.iban}
                                        touched={touched.payment?.iban}
                                      />
                                    </Col>
                                  </>
                                )}
                              </Row>

                              <div className="d-flex align-items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={prevStep}
                                >
                                  Previous
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div
                    className="trip_notes_main_div"
                    style={{ minHeight: "80vh", height: "100%" }}
                  >
                    <h5>Notes</h5>
                    <div style={{ padding: "0 12px 12px" }}>
                      <ReactQuill
                        placeholder="Type your notes or additional info..."
                        modules={modules}
                        theme="snow"
                        value={values.notes || ""}
                        formats={formats}
                        onChange={(content) => setFieldValue("notes", content)}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default AddCustomer;
